import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import PPSaveBar from "@/component/PPSaveBar"
import { usePrompt } from "@/hooks/usePrompt"
import { THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM } from "@/pages/TrackingPage/DisplayOptions/components/themeLayoutAppWindow"
import { getAppLink } from "@/utils/appBridge"

import {
  AppWindowSaveBarMessage,
  AppWindowSaveBarState,
  createAppWindowSaveBarChannel,
  isAppWindowSaveBarMessage,
} from "./appWindowSaveBarBridge"
import { ShopifyNavigationBlocker } from "./NavigationBlocker"

interface ShopifySaveModuleProps {
  onSave: () => void
  onDiscard: () => void
  show: boolean
  unlocking?: boolean
  loading?: boolean
  disabled?: boolean
  subRouteBlock?: boolean
  allowedPaths?: string[]
  isTrackingPage?: boolean
}

const isAppWindowContentFrame = (): boolean => {
  if (typeof window === "undefined") {
    return false
  }

  return new URLSearchParams(window.location.search).get(THEME_LAYOUT_APP_WINDOW_CONTENT_PARAM) === "1"
}

const ShopifySaveModule: React.FC<ShopifySaveModuleProps> = ({
  onSave,
  onDiscard,
  show,
  unlocking = false,
  loading = false,
  disabled = false,
  subRouteBlock = false,
  allowedPaths = [],
  isTrackingPage = false,
}) => {
  const location = useLocation()
  const [isSubRouteNavigating, setIsSubRouteNavigating] = useState(false)
  const isAppWindowChild = useMemo(() => isAppWindowContentFrame(), [])

  const isPathAllowed = useCallback((targetPath: string, currentPath: string) => {
    if (!isTrackingPage || allowedPaths.length === 0) {
      return false
    }

    const normalizedTarget = targetPath.includes(getAppLink())
      ? targetPath.replace(getAppLink(), "/")
      : targetPath

    const normalizedCurrent = currentPath.includes(getAppLink())
      ? currentPath.replace(getAppLink(), "/")
      : currentPath

    return allowedPaths.some(allowedPath => {
      const normalizedAllowedPath = allowedPath.startsWith("/") ? allowedPath : `/${allowedPath}`
      const targetInPath = normalizedTarget.startsWith(normalizedAllowedPath)
      const currentInPath = normalizedCurrent.startsWith(normalizedAllowedPath)

      return targetInPath && currentInPath
    })
  }, [isTrackingPage, allowedPaths])

  const prevPathRef = useRef<string>(location.pathname)

  useEffect(() => {
    const currentPath = location.pathname
    const prevPath = prevPathRef.current

    if (currentPath !== prevPath && isSubRouteNavigating) {
      const timer = setTimeout(() => {
        setIsSubRouteNavigating(false)
      }, 100)

      prevPathRef.current = currentPath
      return () => clearTimeout(timer)
    } else if (currentPath !== prevPath) {
      prevPathRef.current = currentPath
    }
  }, [location.pathname, isSubRouteNavigating])

  const finalUnlocking = unlocking || isSubRouteNavigating

  const onSaveRef = useRef(onSave)
  const onDiscardRef = useRef(onDiscard)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const stateRef = useRef<AppWindowSaveBarState>({
    show,
    loading,
    disabled,
    unlocking: finalUnlocking,
  })

  useEffect(() => {
    onSaveRef.current = onSave
    onDiscardRef.current = onDiscard
  }, [onSave, onDiscard])

  // 弹窗 iframe 模式下，通过 BroadcastChannel 与主应用 frame 双向通信：
  // - 把 SaveBar 状态广播给主应用 frame（由其挂载 Admin 顶部 ContextualSaveBar）
  // - 接收 Admin 顶部的 save / discard 动作，触发本地业务回调
  useEffect(() => {
    if (!isAppWindowChild) {
      return
    }
    const channel = createAppWindowSaveBarChannel()

    if (!channel) {
      return
    }
    channelRef.current = channel

    const handleMessage = (event: MessageEvent<unknown>) => {
      const data = event.data

      if (!isAppWindowSaveBarMessage(data)) {
        return
      }
      if (data.type === "request-state") {
        const stateMessage: AppWindowSaveBarMessage = { type: "state", payload: stateRef.current }

        channel.postMessage(stateMessage)
        return
      }
      if (data.type !== "action") {
        return
      }
      if (data.payload === "save") {
        onSaveRef.current()
        return
      }
      if (data.payload === "discard") {
        onDiscardRef.current()
      }
    }

    channel.addEventListener("message", handleMessage)
    const syncMessage: AppWindowSaveBarMessage = { type: "state", payload: stateRef.current }

    channel.postMessage(syncMessage)

    return () => {
      channel.removeEventListener("message", handleMessage)
      channel.close()
      channelRef.current = null
    }
  }, [isAppWindowChild])

  useEffect(() => {
    if (!isAppWindowChild) {
      return
    }
    const next: AppWindowSaveBarState = {
      show,
      loading,
      disabled,
      unlocking: finalUnlocking,
    }

    stateRef.current = next
    const channel = channelRef.current

    if (!channel) {
      return
    }
    const message: AppWindowSaveBarMessage = { type: "state", payload: next }

    channel.postMessage(message)
  }, [disabled, finalUnlocking, isAppWindowChild, loading, show])

  return (
    <>
      {subRouteBlock && <ShopifyNavigationBlocker when={show} />}

      {!isAppWindowChild && (
        <PPSaveBar
          onSave={onSave}
          onDiscard={onDiscard}
          show={show}
          unlocking={finalUnlocking}
          loading={loading}
          disabled={disabled}
        />
      )}
    </>
  )
}

export default ShopifySaveModule
